import { Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useNotesInfinite } from '@/shared/hooks/useNotesInfinite';
import { useCreateNote } from '@/features/notes/public';
import { groupNotesByDate } from '@/features/notes/public';
import { NotesPanelGroup } from './NotesPanelGroup';

export function NotesPanel() {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotesInfinite();
  const createNote = useCreateNote();

  const notes = data?.pages.flatMap((p) => p.items ?? []) ?? [];
  const groups = groupNotesByDate(notes);

  const handleCreate = () => {
    createNote.mutate(
      { title: null, content: '<p></p>' },
      {
        onSuccess: (note) => {
          if (note.id) navigate(`/${orgId}/notes/${note.id}`);
        },
      },
    );
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden" data-testid="notes-sidebar-panel">
      <div className="px-3 pt-5 pb-3 shrink-0">
        <p className="mb-2 px-2 text-sm font-semibold tracking-widest text-muted-foreground">
          {t('sidebar.notes.title')}
        </p>
        <Button
          type="button"
          size="sm"
          className="w-full justify-start"
          onClick={handleCreate}
          disabled={createNote.isPending}
          data-testid="notes-sidebar-create-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('sidebar.notes.new')}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {isLoading ? (
          <div
            aria-hidden="true"
            className="flex flex-col gap-2"
            data-testid="notes-sidebar-loading-skeleton"
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-1.5 px-3 py-2">
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-2.5 w-full" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <p
            className="px-2 py-2 text-xs text-destructive"
            data-testid="notes-sidebar-error-message"
          >
            {t('sidebar.notes.error')}
          </p>
        ) : notes.length === 0 ? (
          <p
            className="px-2 py-2 text-xs text-muted-foreground"
            data-testid="notes-sidebar-empty-state"
          >
            {t('sidebar.notes.empty')}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((group) => (
              <NotesPanelGroup key={group.key} group={group} />
            ))}

            {hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full justify-start px-2 text-xs text-muted-foreground"
                data-testid="notes-sidebar-load-more"
              >
                {t('sidebar.notes.loadMore')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
